/** biome-ignore-all lint/complexity/noStaticOnlyClass: Utility class for CAIP validation */
import { CAIP_PATTERNS } from "./regex";
import type {
  AccountAddress,
  AccountId,
  AssetId,
  AssetNamespace,
  AssetReference,
  ChainId,
  ChainNamespace,
  ChainReference,
} from "./types";

const CHAIN_NAMESPACES: ReadonlySet<string> = new Set([
  "eip155",
  "solana",
  "cosmos",
  "starknet",
  "sui",
] satisfies ChainNamespace[]);

const ASSET_NAMESPACES: ReadonlySet<string> = new Set([
  "erc20",
  "slip44",
  "spl",
  "ibc",
  "erc721",
  "erc1155",
  "coin",
] satisfies AssetNamespace[]);

export class Validator {
  /**
   * Validates a chain namespace
   */
  static isValidChainNamespace(value: string): value is ChainNamespace {
    return CHAIN_NAMESPACES.has(value);
  }

  /**
   * Validates a chain reference
   */
  static isValidChainReference(value: string): value is ChainReference {
    return CAIP_PATTERNS.CHAIN_REFERENCE.test(value);
  }

  /**
   * Validates an asset namespace
   */
  static isValidAssetNamespace(value: string): value is AssetNamespace {
    return ASSET_NAMESPACES.has(value);
  }

  /**
   * Validates namespace pairs whose reference grammar is chain-specific.
   *
   * `coin` is Glider's deliberate CAIP-19 extension for Sui Move type tags.
   * Keeping the pairing closed prevents a Move type from being accepted on a
   * non-Sui chain (where case normalization can corrupt it), and prevents a
   * Sui asset from being persisted under an unrelated asset namespace.
   */
  static isValidAssetNamespaceForChain(
    chainNamespace: string,
    assetNamespace: string,
  ): boolean {
    return chainNamespace === "sui"
      ? assetNamespace === "coin"
      : assetNamespace !== "coin";
  }

  /**
   * Validates an asset reference
   */
  static isValidAssetReference(
    value: string,
    namespace?: AssetNamespace,
  ): value is AssetReference {
    return namespace === "coin"
      ? CAIP_PATTERNS.MOVE_TYPE_TAG.test(value)
      : CAIP_PATTERNS.REFERENCE.test(value);
  }

  /**
   * Validates an account address
   */
  static isValidAccountAddress(value: string): value is AccountAddress {
    return CAIP_PATTERNS.REFERENCE.test(value);
  }

  /**
   * Validates a Chain ID string
   */
  static isValidChainId(value: string): value is ChainId {
    return CAIP_PATTERNS.CHAIN_ID.test(value);
  }

  /**
   * Validates an Account ID string
   */
  static isValidAccountId(value: string): value is AccountId {
    return CAIP_PATTERNS.ACCOUNT_ID.test(value);
  }

  /**
   * Validates an Asset ID at an untyped input boundary.
   *
   * This validator is also passed directly to `z.custom` and `refine`, where
   * optional or otherwise malformed values can reach it at runtime.
   */
  static isValidAssetId(value: unknown): value is AssetId {
    if (typeof value !== "string") {
      return false;
    }

    const slashIndex = value.indexOf("/");
    if (slashIndex === -1 || slashIndex !== value.lastIndexOf("/")) {
      return false;
    }

    const assetPart = value.slice(slashIndex + 1);
    const namespaceSeparatorIndex = assetPart.indexOf(":");
    if (namespaceSeparatorIndex === -1) {
      return false;
    }

    const assetNamespace = assetPart.slice(0, namespaceSeparatorIndex);
    const chainPart = value.slice(0, slashIndex);
    const chainNamespaceSeparatorIndex = chainPart.indexOf(":");
    if (chainNamespaceSeparatorIndex === -1) {
      return false;
    }
    const chainNamespace = chainPart.slice(0, chainNamespaceSeparatorIndex);
    if (
      !Validator.isValidAssetNamespaceForChain(chainNamespace, assetNamespace)
    ) {
      return false;
    }

    if (assetNamespace !== "coin") {
      return CAIP_PATTERNS.ASSET_ID.test(value);
    }

    const assetReference = assetPart.slice(namespaceSeparatorIndex + 1);
    return (
      CAIP_PATTERNS.CHAIN_ID.test(chainPart) &&
      Validator.isValidAssetReference(assetReference, "coin")
    );
  }
}
